import React, { useEffect, useState } from 'react';
import _ from 'lodash'
import { Plus, Trash2, ChevronDown, Waves, Power, PowerOff, Sun, UsersRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { App, Input, Select } from 'antd';
import { CreateAutomationHeader, CreateAutomationMainSection, CreateAutomationSidebar } from '@/components/autopilot/create-autopilot';
import { Blueprint } from '@/types';
import { useBlueprints } from '@/api/hooks/useBlueprint';
import { useDevices } from '@/api/hooks/useDevices';
import { createAutomation } from '@/api/automation';
import { SUCCESS_MESSAGE } from '@/utils/constants';

type Constraint = {
  id: string;
  device: string;
  attributes: Array<{
    type: 'mode' | 'setpoint';
    value: string | number;
    unit?: string;
  }>;
};

type State = {
  id: string;
  conditions: Array<{
    type: string;
    operator: '>' | '<' | '>=' | '<=';
    value: number;
    unit?: string;
  }>;
  actions: Array<{
    type: string;
    ahus?: string[];
    value?: number;
    unit?: string;
    mode?: 'cool' | 'fan' | 'dry';
  }>;
};

export const blueprints = [
  {
    name: 'Flowsave',
    icon: (<Waves className="h-2 w-2 text-white" />),
    color: 'bg-green',
    description: 'Optimize AHU operation based on environmental conditions',
  },
  {
    name: 'Softstart',
    icon: <Power className="h-2 w-2 text-white" />,
    color: 'bg-mid-blue',
    description: 'Gradually start AHUs to prevent power surge',
  },
  {
    name: 'Softclose',
    icon: <PowerOff className="h-2 w-2 text-white" />,
    color: 'bg-[#5E5E5E]',
    description: 'Gradually shut down AHUs',
  },
  {
    name: 'APL',
    icon: <UsersRound className="h-2 w-2 text-white" />,
    color: 'bg-purple',
    description: 'Occupancy-based load management',
  },
  {
    name: 'ASP',
    icon: <Sun className="h-2 w-2 text-white" />,
    color: 'bg-mid-orange',
    description: 'Solar power utilization optimization',
  },
] as const;

export function CreateAutomation() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [selectedBlueprint, setSelectedBlueprint] = useState<string | null>("Flowsave");
  const [automationName, setAutomationName] = useState('');
  const [interval, setInterval] = useState([0, 0]);
  const [states, setStates] = useState<State[]>([]);
  const [activeTab, setActiveTab] = useState<'states' | 'constraints'>('states');
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  const [parameters, setParameters] = useState([])
  const [availableDevices, setAvailableDevices] = useState([]);
  const [availableModes] = useState(['Cool', 'Fan', 'Dry']);

  const { data: blueprintsData, isLoading: blueprintsLoading, refetch } = useBlueprints();
  const { data: systemData, isLoading, error } = useDevices({ expand: 'devices' });

  const handleBack = () => {
    navigate('/settings/automations');
  };

  const onCreate = async () => {
    try {
      const payload = {
        name: automationName,
        blueprint: blueprintsData.find(blueprint => blueprint.name === selectedBlueprint)?.id,
        config: {
          ...(selectedBlueprint === 'Flowsave' ? { interval: interval[0] * 60 + interval[1] } : {}),
          stages: states.map((state, idx) => ({
            name: `${idx}`,
            actions: selectedBlueprint === 'Flowsave' ? [{
              action_type: "Maintain Fan Unit Count and Temperature Setpoint",
              parameters: Array.isArray(state?.actions) ? state.actions.map(param => ({
                name: param.name,
                value: param.value,
              })) : []
            }] :
              [{
                action_type: state?.actions?.name,
                parameters: state?.actions?.parameters?.map(parameter => ({
                  device_ids: parameter.device_ids,
                  mode: parameter.mode,
                  setpoint: parameter.value,
                }))
              }],
            conditions: _.flatten(state.conditions?.map((condition, conditionIdx) => {
              if (conditionIdx > 0) return [{
                logical_operator: condition.logical_operator,
              }, {
                value: condition.value,
                parameter: condition.parameter,
                operator: condition.operator,
              }]
              else return ({
                value: condition.value,
                parameter: condition.parameter,
                operator: condition.operator,
              })
            })),
            position: idx + 1,
          })),
          constraints: {
            device_constraints: constraints?.map(constraint => ({
              device_ids: constraint.device_ids,
              attributes: constraint.attributes,
            }))
          },
        }
      };
      console.log('Creating automation with payload:', payload);
      await createAutomation(payload);
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
      navigate('/settings/automations');
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  useEffect(() => {
    if (!blueprintsData) return
    const bluePrintData = blueprintsData.find(blueprint => blueprint.name === selectedBlueprint)
    if (!bluePrintData) return
    setInterval([parseInt(bluePrintData.default_config.interval / 60), 0])
    // Set up default states based on blueprint type
    if (selectedBlueprint === 'Flowsave') {
      // For Flowsave: empty conditions, 2 fixed actions
      setStates([{
        id: 'state-1',
        conditions: [],
        actions: [
          {
            name: 'Maintain number of AHU on Fan mode',
            value: 0
          },
          {
            name: 'Set all AHU Setpoint to',
            value: 25
          }
        ]
      }])
    } else {
      // For other blueprints: empty conditions, use action from blueprint data
      const defaultAction = bluePrintData.default_config?.stages?.[0]?.actions?.[0];
      setStates([{
        id: 'state-1',
        conditions: [],
        actions: {
          name: defaultAction?.action_type || '',
          parameters: [
            {
              device_ids: [],
              mode: 'Cool',
              value: 26
            }
          ]
        }
      }])
    }
    setConstraints(bluePrintData.default_config.constraints?.device_constraints?.map(constraint => ({
      device_ids: constraint.device_ids,
      attributes: constraint.attributes,
    })))
    setParameters(bluePrintData.parameters)
  }, [blueprintsData, selectedBlueprint])

  useEffect(() => {
    if (!systemData) return
    setAvailableDevices(systemData.devices?.map(device => ({
      id: device.device_id,
      name: device.name,
    })))
  }, [systemData])

  return (
    <div className="flex flex-col h-full bg-bg">
      {/* Header */}
      <div className="px-4 pt-4">
        <CreateAutomationHeader handleBack={handleBack} navigate={navigate} onCreate={onCreate} />
      </div>
      <div className="flex flex-1 min-h-0">
        {/* Blueprint Selection Sidebar */}
        <CreateAutomationSidebar
          blueprints={blueprints}
          setActiveTab={setActiveTab}
          selectedBlueprint={selectedBlueprint}
          setSelectedBlueprint={setSelectedBlueprint} />

        {/* Main Content */}
        <CreateAutomationMainSection
          automationName={automationName}
          setAutomationName={setAutomationName}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          states={states}
          setStates={setStates}
          interval={interval}
          setInterval={setInterval}
          selectedBlueprint={selectedBlueprint}
          constraints={constraints}
          setConstraints={setConstraints}
          availableDevices={availableDevices}
          parameters={parameters}
        />
      </div>
    </div>
  );
}